/**
 * MilestoneOverlay — full-screen, single-row-at-a-time celebration
 * surface for studio milestones. Mounted once at the app root. Calls
 * useDetectMilestones() so detection runs on every load, and reads
 * useUncelebratedMilestone() to decide whether to show.
 *
 * Tone is restrained: dim backdrop, Nova in the right pose, an
 * editorial line, and a single "Got it" pill. No confetti.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  useDetectMilestones,
  useUncelebratedMilestone,
  markMilestoneCelebrated,
  MILESTONE_COPY,
  type MilestoneKind,
  type StudioMilestoneRow,
} from '@/services/studioMilestones';
import { cn } from '@/lib/utils';
import { NovaExpression } from '@/components/nova/kit';
import { mapNovaState } from '@/components/nova/kit/novaStateMap';

const REVENUE_KINDS = new Set<MilestoneKind>([
  'revenue_1k',
  'revenue_10k',
  'revenue_50k',
  'revenue_100k',
]);
const COUNT_KINDS = new Set<MilestoneKind>([
  'clients_10',
  'clients_50',
  'clients_100',
  'projects_10',
  'projects_50',
  'projects_100',
  'paid_invoices_10',
  'paid_invoices_50',
]);

function detailFor(row: StudioMilestoneRow): string | null {
  const v = row.value as { revenue_cents?: number; count?: number } | null;
  if (!v) return null;
  if (REVENUE_KINDS.has(row.kind) && typeof v.revenue_cents === 'number') {
    const dollars = Math.floor(v.revenue_cents / 100);
    const formatted = dollars.toLocaleString('en-US');
    return `Lifetime · $${formatted}`;
  }
  if (COUNT_KINDS.has(row.kind) && typeof v.count === 'number') {
    return `${v.count.toLocaleString('en-US')} and counting`;
  }
  return null;
}

export function MilestoneOverlay() {
  useDetectMilestones();
  const { data: row } = useUncelebratedMilestone();
  const qc = useQueryClient();

  const [closing, setClosing] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Reset transient state whenever a new milestone arrives.
  useEffect(() => {
    if (row?.id && row.id !== activeId) {
      setActiveId(row.id);
      setClosing(false);
    }
  }, [row?.id, activeId]);

  // Auto-dismiss after 4.5s.
  useEffect(() => {
    if (!row || closing) return;
    const t: ReturnType<typeof setTimeout> = setTimeout(() => beginDismiss(), 4500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id, closing]);

  if (!row) return null;

  const copy = MILESTONE_COPY[row.kind] ?? null;
  const detail = detailFor(row);

  function beginDismiss() {
    if (!row || closing) return;
    setClosing(true);
    const id = row.id;
    // Mirror the CSS transition so the moment doesn't blink off.
    setTimeout(async () => {
      await markMilestoneCelebrated(id);
      qc.invalidateQueries({ queryKey: ['studio-milestones', 'uncelebrated'] });
      setClosing(false);
    }, 300);
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex items-center justify-center px-4',
        'bg-black/55',
        'transition-opacity duration-[280ms] ease-out',
        closing ? 'opacity-0' : 'opacity-100',
      )}
      onClick={beginDismiss}
      role="dialog"
      aria-modal="true"
      aria-label={copy?.headline ?? 'Milestone reached'}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative w-full max-w-md rounded-3xl border border-border bg-card',
          'p-8 md:p-10 shadow-2xl text-center',
          'transition-all duration-[280ms] ease-out',
          closing ? 'opacity-0 scale-[0.97]' : 'opacity-100 scale-100',
        )}
      >
        <div className="flex justify-center mb-6">
          <NovaExpression id={mapNovaState(copy?.novaState ?? 'celebrating')} size={108} />
        </div>

        <h2 className="font-editorial text-3xl md:text-4xl text-foreground leading-tight">
          {copy?.headline ?? row.label}
        </h2>

        {copy?.subline && (
          <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
            {copy.subline}
          </p>
        )}

        {detail && (
          <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-orbit-orange">
            {detail}
          </p>
        )}

        <div className="mt-8">
          <Button
            type="button"
            onClick={beginDismiss}
            className="rounded-full px-6 bg-orbit-orange text-background hover:bg-orbit-orange/90"
          >
            Got it
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MilestoneOverlay;